import React from 'react';
import {
    Box,
    Container,
    Grid,
    Paper,
    Stack,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Card, CardContent
} from '@mui/material';
import {
    InfoOutlined,
    MemoryOutlined,
    GroupOutlined,
    SecurityOutlined,
    TrendingUpOutlined,
    TerminalOutlined,
    CheckCircleOutline
} from '@mui/icons-material';

const RQCard = ({ title, questions, methods }) => (
    <Card elevation={0} sx={{ border: '1px solid #e0e0e0', height: '100%' }}>
        <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
                {title}
            </Typography>
            <Stack spacing={1} mb={2}>
                {questions.map((q, i) => (
                    <Typography key={i} variant="body2" color="text.secondary">
                        <strong>RQ{i + 1}:</strong> {q}
                    </Typography>
                ))}
            </Stack>
            <Typography variant="caption" color="text.secondary">
                {methods}
            </Typography>
        </CardContent>
    </Card>
);

export default function About() {
    const researchQuestions = [
        {
            title: "A. Multimodal Sensing & Signal Quality",
            questions: [
                "Which minimal multimodal bundle best explains short‑term affect shifts and reframing adherence?",
                "How do sensor quality, missingness, and burden affect model reliability and user acceptance over weeks?",
                "Can actionable sensing—signals suggesting clear actions—improve user-perceived control compared to purely predictive sensing?"
            ],
            methods: "Methods: feature ablations, mixed‑effects modeling, surveys, and compliance analytics."
        },
        {
            title: "B. LLM/ML Design for Therapeutic Support",
            questions: [
                "Do LLM-generated prompts like Socratic questions outperform templated prompts on engagement and helpfulness?",
                "How do explanations, such as rationales and uncertainty cues, influence trust, autonomy, and over-reliance?",
                "Can speech-to-text plus paralinguistic cues improve reframing quality without increasing burden?"
            ],
            methods: "Methods: prompt A/B testing, uncertainty UI evaluation, speech vs text input studies, clinician ratings."
        },
        {
            title: "C. Forecasting & Just‑In‑Time Adaptive Interventions",
            questions: [
                "What horizons (12–72 hours) are feasible for affect or rumination risk forecasts from daily traces?",
                "When do timing and channel adaptations reduce interruption cost yet preserve benefit?",
                "Can counterfactual policy evaluation estimate JITAI policies before deployment?"
            ],
            methods: "Methods: temporal transformers, calibration, off-policy evaluation, micro-randomized trials."
        },
        {
            title: "D. Interaction Design, Safety, and Trust",
            questions: [
                "Which micro-interactions, like progressive disclosure and redo options, lower cognitive load and increase felt safety?",
                "How do safety filters such as self-harm screening affect trust and disclosure?",
                "What human-in-the-loop escalation patterns balance support with privacy?"
            ],
            methods: "Methods: think-aloud, trust scales, edge-case audits, and crisis usability tests."
        },
        {
            title: "E. Equity, Personalization, and Generalization",
            questions: [
                "Do outcomes vary across demographics, culture, and language? How can UI and LLM adapt without stereotyping?",
                "Can personalized reframing styles improve adherence for subgroups?",
                "How robust are models under distribution shift and sensor dropout?"
            ],
            methods: "Methods: fairness audits, multilingual evaluation, personalization, and stress tests."
        }
    ];

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Stack spacing={4}>
                {/* Header */}
                <Paper elevation={0} sx={{ p: { xs: 4, md: 6 }, textAlign: 'center', backgroundColor: 'grey.50' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
                        Cognitive Reframing for Mental Health HCI
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        A research-forward specification for multimodal sensing and actionable mental-health computing.
                    </Typography>
                </Paper>

                {/* Introduction */}
                <Paper elevation={0} sx={{ p: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                        <InfoOutlined color="primary" />
                        <Typography variant="h5" component="h2">1) Introduction</Typography>
                    </Stack>
                    <Typography variant="body1" color="text.secondary">
                        This document reframes the Cognitive Reframing project as a <strong>research testbed</strong> for Human–Computer Interaction (HCI) in mental health. The interactive experience scaffolds users through identifying, examining, and restructuring negative thought patterns. Our future goals are to extend the scope beyond text-only interaction to include <strong>multimodal sensing</strong> (smartphone, wearables, environment) and to enhance our <strong>LLM/ML</strong> components to study efficacy, personalization, safety, and ethics at scale.
                    </Typography>
                </Paper>

                {/* Platform Capabilities */}
                <Paper elevation={0} sx={{ p: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                        <MemoryOutlined color="secondary" />
                        <Typography variant="h5" component="h2">2) Research Platform Capabilities</Typography>
                    </Stack>
                    <List>
                        {[
                            "Thought capture: free text and voice input with structured prompts.",
                            "AI guidance: LLMs provide gentle rewordings and Socratic cues.",
                            "Community layer: anonymized exemplars for social learning.",
                            "Future capability: Multimodal sensing: passive and active signals from phone and wearables.",
                            "Forecasting & JITAI hooks: risk and need forecasting for just-in-time adaptive interventions.",
                            "Governance: consent flows and privacy-preserving aggregation."
                        ].map((text, index) => (
                            <ListItem key={index} disablePadding>
                                <ListItemIcon>
                                    <CheckCircleOutline fontSize="small" color="success" />
                                </ListItemIcon>
                                <ListItemText primary={text} />
                            </ListItem>
                        ))}
                    </List>
                </Paper>

                {/* Research Questions */}
                <Paper elevation={0} sx={{ p: 3, backgroundColor: 'grey.50' }}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" mb={3}>
                        <GroupOutlined color="action" />
                        <Typography variant="h5" component="h2" align="center">3) Core Research Questions (RQs)</Typography>
                    </Stack>
                    <Grid container spacing={3}>
                        {researchQuestions.map((rq, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <RQCard title={rq.title} questions={rq.questions} methods={rq.methods} />
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                {/* Measures & Study Design and Data, Privacy, and Ethics */}
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 3, height: '100%' }}>
                            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                <TrendingUpOutlined sx={{ color: 'warning.main' }} />
                                <Typography variant="h5" component="h2">4) Measures & Study Design</Typography>
                            </Stack>
                            <List dense>
                                {[
                                    "Clinical/psychometric: PHQ‑9, GAD‑7, PANAS, etc.",
                                    "Behavioral: engagement metrics, session length, etc.",
                                    "Sensing: sleep, HRV, steps, etc.",
                                    "Model performance: calibration, AUROC/PR, etc.",
                                    "Qualitative: interviews, diary studies, etc."
                                ].map((text, index) => (
                                    <ListItem key={index} disablePadding>
                                        <ListItemText primary={text} />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 3, height: '100%' }}>
                            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                <SecurityOutlined color="error" />
                                <Typography variant="h5" component="h2">5) Data, Privacy, and Ethics</Typography>
                            </Stack>
                            <List dense>
                                {[
                                    "Consent & minimization: tiered consent and on‑device processing.",
                                    "Safety: self‑harm screening and crisis resources.",
                                    "Governance: IRB alignment and fairness audits.",
                                    "Participant control: data export and deletion."
                                ].map((text, index) => (
                                    <ListItem key={index} disablePadding>
                                        <ListItemText primary={text} />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Implementation Notes */}
                <Paper elevation={0} sx={{ p: 3, backgroundColor: 'grey.900', color: 'white' }}>
                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                        <TerminalOutlined />
                        <Typography variant="h5" component="h2">6) Implementation Notes (LLM/ML)</Typography>
                    </Stack>
                    <Box sx={{ fontFamily: 'monospace', fontSize: 'body2.fontSize' }}>
                        <Typography component="p" sx={{ color: 'success.light' }}>Pipelines: ingestion, feature storage, forecasting, and policy engines.</Typography>
                        <Typography component="p" sx={{ color: 'success.light' }}>LLM prompting: guardrails, style controls, and uncertainty cues.</Typography>
                        <Typography component="p" sx={{ color: 'success.light' }}>Evaluation: randomized prompt arms and human ratings.</Typography>
                        <Typography component="p" sx={{ color: 'success.light' }}>Privacy tech: on‑device embeddings and federated updates.</Typography>
                    </Box>
                </Paper>

                {/* Footer */}
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>
                    Last updated: {new Date().toLocaleDateString()}
                </Typography>
            </Stack>
        </Container>
    );
}